import { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabase";
import "./ContactReplyPanel.css";

const API = process.env.REACT_APP_API_URL || "";
const STATUS = { sent: "발송 완료", failed: "발송 실패", sending: "발송 결과 확인 필요", uncertain: "발송 결과 확인 필요" };
const date = (value) => new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });

async function requestReplies(id, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("관리자 로그인이 필요합니다.");
  const response = await fetch(`${API}/api/admin/contact-inquiries/${id}/replies`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(typeof data?.detail === "string" ? data.detail : "답변 이력을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.");
    error.status = response.status;
    throw error;
  }
  if (!data) throw new Error("서버 응답을 확인하지 못했습니다.");
  return data;
}

export default function ContactReplyPanel({ inquiry, draft, onDraft, onBusy, onSent }) {
  const [replies, setReplies] = useState([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const input = useRef(null);
  const lock = useRef(false);
  const callbacks = useRef({ onDraft, onSent });
  callbacks.current = { onDraft, onSent };
  const text = draft?.text || "";
  const attempt = draft?.attempt;
  const unresolved = replies.some((reply) => ["sending", "uncertain"].includes(reply.status));

  useEffect(() => {
    const controller = new AbortController();
    requestReplies(inquiry.id, { signal: controller.signal }).then((data) => {
      if (controller.signal.aborted) return;
      setReplies(data.replies); setReady(data.mail_ready); setLoading(false);
    }).catch((err) => {
      if (!controller.signal.aborted) { setError(err.message); setLoading(false); }
    });
    return () => controller.abort();
  }, [inquiry.id]);

  useEffect(() => {
    if (input.current) {
      input.current.style.height = "auto";
      input.current.style.height = `${Math.min(320, Math.max(115, input.current.scrollHeight))}px`;
    }
  }, [text, loading]);

  function acceptOutcome(reply) {
    setReplies((rows) => [...rows.filter((r) => r.id !== reply.id), reply].sort((a, b) => a.created_at.localeCompare(b.created_at)));
    if (reply.status === "sent") {
      callbacks.current.onDraft({ text: "", attempt: null });
      callbacks.current.onSent();
      setNotice("메일을 발송했습니다.");
    } else if (reply.status === "failed") {
      callbacks.current.onDraft({ text: reply.body, attempt: null });
      setError("메일 발송에 실패했습니다. 작성한 내용은 보존했습니다. 발송 설정을 확인한 뒤 다시 보내 주세요.");
    } else {
      setError("발송 여부 확인이 필요합니다. 중복 발송을 막기 위해 보내기를 잠갔습니다. 관리자가 발송 기록을 확인해야 합니다.");
    }
  }

  async function refresh() {
    if (lock.current) return;
    lock.current = true; setBusy(true); onBusy(true); setError(""); setNotice("");
    try {
      const data = await requestReplies(inquiry.id);
      setReplies(data.replies); setReady(data.mail_ready); setLoading(false);
      const found = attempt && data.replies.find((r) => r.id === attempt.id);
      if (found) acceptOutcome(found);
      else if (attempt) setNotice("발송 기록이 아직 없습니다. 같은 요청으로 다시 확인·발송할 수 있습니다.");
    } catch (err) { setError(err.message); }
    finally { lock.current = false; setBusy(false); onBusy(false); }
  }

  async function send(event) {
    event.preventDefault();
    if (lock.current || !ready || loading || unresolved || !text.trim()) return;
    const next = attempt || { id: crypto.randomUUID(), body: text.trim() };
    lock.current = true; setBusy(true); onBusy(true); setError(""); setNotice("");
    onDraft({ text, attempt: next });
    try {
      const data = await requestReplies(inquiry.id, {
        method: "POST", body: JSON.stringify({ request_id: next.id, message: next.body }),
      });
      acceptOutcome(data.reply);
    } catch (err) {
      // Auth/validation errors occur before reservation; other failures may be ambiguous.
      if ([400, 401, 403, 404, 422].includes(err.status)) onDraft({ text, attempt: null });
      // Retain the request ID for ambiguous outcomes. Retrying cannot send twice.
      setError(`${err.message} ‘이력 새로고침’으로 결과를 먼저 확인해 주세요.`);
    } finally { lock.current = false; setBusy(false); onBusy(false); }
  }

  return (
    <section className="contact-reply">
      <div className="contact-reply-heading">
        <h3>답변 이력</h3>
        <button type="button" className="contact-reply-refresh" onClick={refresh} disabled={busy}>이력 새로고침</button>
      </div>
      {loading ? <p className="contact-reply-muted">불러오는 중...</p> : replies.length === 0 ? <p className="contact-reply-muted">아직 보낸 답변이 없습니다.</p> : replies.map((reply) => (
        <div className="contact-reply-history" key={reply.id}>
          <div className="contact-reply-meta">{date(reply.sent_at || reply.created_at)} · {reply.sender}
            <span className={`contact-reply-status ${reply.status}`}>{STATUS[reply.status]}</span>
          </div>
          <p>{reply.body}</p>
        </div>
      ))}
      <form className="contact-reply-form" onSubmit={send}>
        <h3>답변 작성</h3>
        <div className="contact-reply-muted">보내는 주소 · 3d@makerz.co.kr</div>
        <label className="contact-reply-label" htmlFor="contact-reply-body">받는 고객 · {inquiry.user_email || "이메일 없음"}</label>
        <textarea id="contact-reply-body" ref={input} value={text} maxLength={10000} rows={4}
          readOnly={busy || !!attempt || unresolved} placeholder="고객에게 보낼 답변을 작성해 주세요."
          onChange={(e) => onDraft({ text: e.target.value, attempt: null })} />
        {!loading && !ready && <p className="contact-reply-error">문의 답변용 메일 발송 설정이 필요합니다.</p>}
        {unresolved && <p className="contact-reply-error">발송 여부가 확정되지 않은 답변이 있습니다. 중복 발송을 막기 위해 결과 확인 후 추가 답변을 보낼 수 있습니다.</p>}
        {error && <p role="alert" className="contact-reply-error">{error}</p>}
        {notice && <p role="status" className="contact-reply-notice">{notice}</p>}
        <div className="contact-reply-footer">
          <small>고객의 재답장은 3d 공용 메일함으로 도착합니다.</small>
          <button type="submit" disabled={busy || loading || !ready || unresolved || !text.trim() || !inquiry.user_email}>
            {busy ? "처리 중…" : attempt ? "같은 요청 다시 확인" : "메일로 보내기"}
          </button>
        </div>
      </form>
    </section>
  );
}
